import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import {
  DriverStatus,
  type DataFileListItem,
  type TelemetrySessionContext,
} from '@kangaroo/shared';
import { getSessionDrawerChrome, sessionDrawerSelectSx } from '@kangaroo/ui';

import { driverListKey, fetchDrivers } from '../../features/drivers';
import { fetchKarts, kartsListKey } from '../../features/karts';
import { currentTeamKey, fetchCurrentTeam } from '../../features/teams';
import {
  telemetrySessionContextKey,
  fetchTelemetrySessionContext,
  updateTelemetrySessionContext,
} from '../../features/telemetryContext';
import {
  canClearDriverSelection,
  filterDriversForUser,
  filterKartsForDriver,
  isDriverSelectionLocked,
} from '../../lib/driverAccess';
import { canManageKarts } from '../../lib/roles';
import { viewerIsTeamWorkspaceOwner } from '../../lib/teamWorkspace';
import {
  buildSessionAssociationUpdates,
  formatKartSubtitle,
} from '../../lib/telemetrySessions';
import { sessionRecordedAtMs, type MyChronSessionExport } from '../../lib/telemetrySession';
import { matchSessionToCircuitDraft, type CircuitDraftMatch } from '../../lib/trackEnrichment';
import {
  listAssignableCircuitContributions,
  rankSessionCircuitPickerContributions,
  suggestCircuitContributionForSession,
} from '../../lib/circuitMapOverlay';
import { formatCircuitPickerMenuLabel, statusLabel } from '../../lib/circuits';
import { useTrackContributionCatalog } from '../../hooks/useTrackContributionCatalog';
import { useAuthStore } from '../../store/authStore';
import { SessionKartSetupPanel } from './SessionKartSetupPanel';

type TelemetrySessionContextPanelProps = {
  sessionKey: string;
  session: MyChronSessionExport;
  sessionContext: TelemetrySessionContext | null;
  dataFile: DataFileListItem | null;
  canEdit: boolean;
  variant?: 'default' | 'drawer' | 'sidebar';
  hideCircuitAssignment?: boolean;
  showVehicleSetup?: boolean;
  onSaved?: (saved: { context: TelemetrySessionContext; driverName: string | null }) => void;
};

export function TelemetrySessionContextPanel({
  sessionKey,
  session,
  sessionContext,
  dataFile,
  canEdit,
  variant = 'default',
  hideCircuitAssignment = false,
  showVehicleSetup = true,
  onSaved,
}: TelemetrySessionContextPanelProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const drawerChrome = getSessionDrawerChrome(isDark);
  const user = useAuthStore((state) => state.user);
  const { data: teamProfile } = useSWR(currentTeamKey, fetchCurrentTeam);
  const isWorkspaceOwner = viewerIsTeamWorkspaceOwner(user, teamProfile);
  const autoAppliedRef = useRef<string | null>(null);
  const circuitAutoAppliedRef = useRef<string | null>(null);
  const canManageVehicle = user?.role ? canManageKarts(user.role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;

  const dataFileDriverId = dataFile?.sessionAssignment
    ? dataFile.sessionAssignment.driverId
    : dataFile?.driverId ?? null;
  const contextDriverId = sessionContext?.driverAssignmentSet === true
    ? sessionContext.driverId
    : sessionContext?.driverId ?? dataFileDriverId;
  const contextKartId = sessionContext?.kartId ?? dataFile?.kartId ?? null;
  const contextKartSetupHistoryEntryId = sessionContext?.kartSetupHistoryEntryId ?? null;
  const contextContributionId = sessionContext?.contributionId ?? null;
  const recordedAtMs = sessionContext?.recordedAtMs ?? sessionRecordedAtMs(session) ?? null;

  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(contextDriverId);
  const [selectedKartId, setSelectedKartId] = useState<string | null>(contextKartId);
  const [selectedKartSetupHistoryEntryId, setSelectedKartSetupHistoryEntryId] = useState<string | null>(contextKartSetupHistoryEntryId);
  const [selectedContributionId, setSelectedContributionId] = useState<string | null>(contextContributionId);
  const [suggestedCircuitMatch, setSuggestedCircuitMatch] = useState<CircuitDraftMatch | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedDriverId(contextDriverId);
    setSelectedKartId(contextKartId);
    setSelectedKartSetupHistoryEntryId(contextKartSetupHistoryEntryId);
    setSelectedContributionId(contextContributionId);
    setError(null);
  }, [contextContributionId, contextDriverId, contextKartId, contextKartSetupHistoryEntryId, sessionKey]);

  const { data: driversPage, isLoading: driversLoading } = useSWR(
    driverListKey({ status: DriverStatus.ACTIVE, page: 1, perPage: 100 }),
    () => fetchDrivers({ status: DriverStatus.ACTIVE, page: 1, perPage: 100 }),
  );
  const { data: kartsPage, isLoading: kartsLoading } = useSWR(
    kartsListKey({ page: 1, perPage: 100 }),
    () => fetchKarts({ page: 1, perPage: 100 }),
  );
  const { mutate: mutateContext } = useSWR(
    telemetrySessionContextKey(sessionKey),
    () => fetchTelemetrySessionContext(sessionKey),
  );
  const sessionGps = useMemo(
    () => session.gps?.map((point) => ({ lat: point.lat, lon: point.lon })) ?? null,
    [session.gps],
  );
  const sessionTrackName = dataFile?.summary.trackName ?? session.metadata?.venue ?? null;
  const contributionCatalog = useTrackContributionCatalog({
    contributionIds: [
      contextContributionId,
      selectedContributionId,
      sessionContext?.circuitLapSet?.contributionId,
    ],
    trackId: sessionContext?.trackId,
    sessionGps,
  });
  const contributionsLoading = contributionCatalog.isLoading || contributionCatalog.isHydrating;
  const mergedTrackContributions = contributionCatalog.contributions;

  const assignableCircuits = useMemo(
    () => listAssignableCircuitContributions(mergedTrackContributions),
    [mergedTrackContributions],
  );
  const circuitPickerOptions = useMemo(() => {
    const ranked = rankSessionCircuitPickerContributions(mergedTrackContributions, {
      assignedContributionId: contextContributionId,
      session,
      sessionTrackName,
      trackId: sessionContext?.trackId,
    });
    if (contextContributionId && !ranked.some((entry) => entry.contribution.id === contextContributionId)) {
      const assigned = mergedTrackContributions.find((contribution) => contribution.id === contextContributionId);
      return assigned
        ? [{ contribution: assigned, distanceKm: null, recent: false }, ...ranked]
        : ranked;
    }
    return ranked;
  }, [
    contextContributionId,
    mergedTrackContributions,
    session,
    sessionContext?.trackId,
    sessionTrackName,
  ]);
  const searchableCircuitOptions = useMemo(() => {
    const ordered = new Map(
      circuitPickerOptions.map((entry) => [entry.contribution.id, entry.contribution]),
    );
    const remaining = [...mergedTrackContributions]
      .filter((contribution) => (
        (contribution.payload.polylinePointCount ?? contribution.payload.gpsPolyline.length) >= 2
        || (contribution.payload.detailedGeometryFeatureCount ?? 0) > 0
      ))
      .sort((left, right) => {
        const venue = (left.venue?.trim() || 'Saved circuit')
          .localeCompare(right.venue?.trim() || 'Saved circuit');
        return venue || (left.layoutName?.trim() || '').localeCompare(right.layoutName?.trim() || '');
      });
    for (const contribution of remaining) {
      if (!ordered.has(contribution.id)) {
        ordered.set(contribution.id, contribution);
      }
    }
    return [...ordered.values()];
  }, [circuitPickerOptions, mergedTrackContributions]);
  const selectedContribution =
    assignableCircuits.find((contribution) => contribution.id === selectedContributionId)
    ?? mergedTrackContributions.find((contribution) => contribution.id === selectedContributionId)
    ?? null;
  const assignedContributionLabel = selectedContribution
    ? `${selectedContribution.venue?.trim() || 'Saved circuit'} (${statusLabel(selectedContribution.status)})`
    : null;

  const suggestedCircuit = useMemo(
    () => suggestCircuitContributionForSession({
      session,
      contributions: mergedTrackContributions,
      sessionTrackName,
      trackId: sessionContext?.trackId,
    }),
    [mergedTrackContributions, session, sessionContext?.trackId, sessionTrackName],
  );

  const circuitMatch = useMemo(
    () => matchSessionToCircuitDraft(session, assignableCircuits),
    [assignableCircuits, session],
  );

  useEffect(() => {
    setSuggestedCircuitMatch(circuitMatch);
  }, [circuitMatch, sessionKey]);

  useEffect(() => {
    if (!canEdit || contextContributionId || !suggestedCircuit) {
      return;
    }

    setSelectedContributionId((current) => current || suggestedCircuit.id);
  }, [canEdit, contextContributionId, suggestedCircuit, sessionKey]);

  const drivers = useMemo(
    () => filterDriversForUser(driversPage?.data ?? [], user),
    [driversPage?.data, user],
  );
  const karts = kartsPage?.data ?? [];
  const selectedDriver = drivers.find((driver) => driver.id === selectedDriverId) ?? null;
  const visibleKarts = useMemo(
    () => filterKartsForDriver(karts, selectedDriver),
    [karts, selectedDriver],
  );
  const activeKart = karts.find((kart) => kart.id === (selectedKartId ?? contextKartId)) ?? null;
  const selectedContributionMissing = Boolean(selectedContributionId) && !selectedContribution;
  const selectedDriverMissing = Boolean(selectedDriverId) && !selectedDriver;
  const selectedKartMissing = Boolean(selectedKartId) && !visibleKarts.some((kart) => kart.id === selectedKartId);
  const driverSelectionLocked = isDriverSelectionLocked(user);
  const allowClearDriver = canClearDriverSelection(user);

  useEffect(() => {
    if (!canEdit || !sessionContext || driversLoading || kartsLoading) {
      return;
    }

    if (autoAppliedRef.current === sessionKey) {
      return;
    }

    const updates = buildSessionAssociationUpdates(
      session,
      drivers,
      karts,
      {
        driverId: sessionContext.driverId,
        driverAssignmentSet: sessionContext.driverAssignmentSet,
        kartId: sessionContext.kartId,
      },
      dataFile,
    );

    autoAppliedRef.current = sessionKey;

    if (!updates) {
      return;
    }

    void updateTelemetrySessionContext(sessionKey, updates)
      .then((nextContext) => mutateContext(nextContext, { revalidate: false }))
      .catch((saveError) => {
        setError(saveError instanceof Error ? saveError.message : 'Could not auto-assign session.');
      });
  }, [
    canEdit,
    dataFile,
    drivers,
    driversLoading,
    karts,
    kartsLoading,
    mutateContext,
    session,
    sessionContext,
    sessionKey,
  ]);

  useEffect(() => {
    if (!canEdit || !sessionContext || contributionsLoading || circuitAutoAppliedRef.current === sessionKey) {
      return;
    }

    if (contextContributionId || !circuitMatch) {
      circuitAutoAppliedRef.current = sessionKey;
      return;
    }

    circuitAutoAppliedRef.current = sessionKey;
    setSelectedContributionId(circuitMatch.draftId);
    void updateTelemetrySessionContext(sessionKey, {
      contributionId: circuitMatch.draftId,
      trackId: circuitMatch.trackId,
    })
      .then((nextContext) => mutateContext(nextContext, { revalidate: false }))
      .catch((saveError) => {
        setError(saveError instanceof Error ? saveError.message : 'Could not assign matched circuit.');
      });
  }, [
    canEdit,
    circuitMatch,
    contextContributionId,
    contributionsLoading,
    mutateContext,
    sessionContext,
    sessionKey,
  ]);

  async function saveAssignment(overrides?: {
    driverId?: string | null;
    kartId?: string | null;
    kartSetupHistoryEntryId?: string | null;
    contributionId?: string | null;
  }) {
    const nextDriverId = overrides?.driverId !== undefined ? overrides.driverId : selectedDriverId;
    const nextKartId = overrides?.kartId !== undefined ? overrides.kartId : selectedKartId;
    const nextKartSetupHistoryEntryId =
      overrides?.kartSetupHistoryEntryId !== undefined
        ? overrides.kartSetupHistoryEntryId
        : selectedKartSetupHistoryEntryId;
    const nextContributionId =
      overrides?.contributionId !== undefined ? overrides.contributionId : selectedContributionId;
    const nextContribution =
      assignableCircuits.find((contribution) => contribution.id === nextContributionId) ?? null;

    try {
      setSaving(true);
      setError(null);
      const nextContext = await updateTelemetrySessionContext(sessionKey, {
        dataFileId: dataFile?.id ?? sessionContext?.dataFileId ?? null,
        driverId: nextDriverId,
        kartId: nextKartId,
        kartSetupHistoryEntryId: nextKartSetupHistoryEntryId,
        contributionId: nextContributionId,
        trackId: nextContribution?.trackId ?? null,
        recordedAtMs,
      });
      await mutateContext(nextContext, { revalidate: false });
      onSaved?.({
        context: nextContext,
        driverName: drivers.find((driver) => driver.id === nextContext.driverId)?.name ?? null,
      });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save assignment.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    await saveAssignment();
  }

  async function handleSidebarDriverChange(driverId: string | null) {
    setSelectedDriverId(driverId);

    let nextKartId: string | null = null;
    if (!driverId) {
      setSelectedKartId(null);
      setSelectedKartSetupHistoryEntryId(null);
    } else {
      const driver = drivers.find((entry) => entry.id === driverId) ?? null;
      const driverKarts = filterKartsForDriver(karts, driver);
      const defaultKart = driverKarts[0];
      if (
        defaultKart
        && (!selectedKartId || !driverKarts.some((kart) => kart.id === selectedKartId))
      ) {
        nextKartId = defaultKart.id;
        setSelectedKartId(defaultKart.id);
        setSelectedKartSetupHistoryEntryId(null);
      } else {
        nextKartId = selectedKartId;
      }
    }

    await saveAssignment({ driverId, kartId: nextKartId, kartSetupHistoryEntryId: null });
  }

  const assignmentTitle = variant === 'drawer' || variant === 'sidebar' ? 'Assignment' : 'Session assignment';
  const compactVariant = variant === 'drawer' || variant === 'sidebar';
  const labelFontSize = variant === 'sidebar' ? '0.76rem' : compactVariant ? '0.68rem' : '0.78rem';
  const helperFontSize = variant === 'sidebar' ? '0.72rem' : compactVariant ? '0.62rem' : '0.72rem';
  const bodyFontSize = variant === 'sidebar' ? '0.82rem' : compactVariant ? '0.72rem' : '0.72rem';
  const metaFontSize = variant === 'sidebar' ? '0.74rem' : compactVariant ? '0.58rem' : '0.68rem';
  const fieldLabelSx = variant === 'drawer'
    ? { fontSize: labelFontSize, fontWeight: 700, mb: 0.5, color: drawerChrome.label }
    : { fontSize: labelFontSize, fontWeight: 700, mb: 0.5 };
  const fieldHelperSx = variant === 'drawer'
    ? { fontSize: helperFontSize, color: drawerChrome.body, mb: 0.75, lineHeight: 1.45 }
    : { fontSize: helperFontSize, color: 'text.secondary', mb: 0.75, lineHeight: 1.45 };
  const fieldMetaSx = variant === 'drawer'
    ? { fontSize: metaFontSize, color: drawerChrome.body, mt: 0.5, lineHeight: 1.45 }
    : { fontSize: metaFontSize, color: 'text.secondary', mt: 0.5, lineHeight: 1.45 };
  const drawerSelectSx = variant === 'drawer' ? sessionDrawerSelectSx(isDark) : undefined;

  if (variant === 'sidebar') {
    const driverName = drivers.find((driver) => driver.id === contextDriverId)?.name ?? 'Unassigned';
    const kartLabel = activeKart
      ? (activeKart.kartNumber ? `#${activeKart.kartNumber}` : activeKart.nickname ?? 'Vehicle')
      : 'Unassigned';
    const assignedCircuit = assignableCircuits.find((contribution) => contribution.id === contextContributionId);
    const circuitName = assignedCircuit
      ? `${assignedCircuit.venue?.trim() || 'Saved circuit'} · ${statusLabel(assignedCircuit.status)}`
      : 'Unassigned';

    if (!canEdit) {
      return (
        <Stack spacing={1}>
          <Stack spacing={0.85}>
            <Typography sx={{ fontSize: bodyFontSize, color: '#CBD5E1' }}>
              <Box component="span" sx={{ color: '#64748B' }}>Circuit · </Box>
              {circuitName}
            </Typography>
            <Typography sx={{ fontSize: bodyFontSize, color: '#CBD5E1' }}>
              <Box component="span" sx={{ color: '#64748B' }}>Driver · </Box>
              {driverName}
            </Typography>
            <Typography sx={{ fontSize: bodyFontSize, color: '#CBD5E1' }}>
              <Box component="span" sx={{ color: '#64748B' }}>Vehicle · </Box>
              {kartLabel}
            </Typography>
          </Stack>
          <SessionKartSetupPanel
            kart={activeKart}
            recordedAtMs={recordedAtMs}
            setupHistoryEntryId={selectedKartSetupHistoryEntryId}
            variant="compact"
          />
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        {error ? <Alert severity="error" sx={{ py: 0.35 }}>{error}</Alert> : null}

        {suggestedCircuitMatch && selectedContributionId === suggestedCircuitMatch.draftId ? (
          <Typography sx={{ fontSize: helperFontSize, color: '#64748B', lineHeight: 1.4 }}>
            GPS match · {suggestedCircuitMatch.circuitName} ({Math.round(suggestedCircuitMatch.confidence * 100)}%)
          </Typography>
        ) : null}

        <FormControl size="small" fullWidth>
          <Typography sx={{ fontSize: labelFontSize, fontWeight: 650, mb: 0.35, color: '#94A3B8' }}>
            Circuit
          </Typography>
          <Autocomplete
            options={searchableCircuitOptions}
            value={searchableCircuitOptions.find((item) => item.id === selectedContributionId) ?? null}
            loading={contributionsLoading}
            disabled={contributionsLoading || saving}
            autoHighlight
            openOnFocus
            getOptionLabel={(contribution) => formatCircuitPickerMenuLabel(contribution)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, state) => {
              const query = state.inputValue.trim().toLocaleLowerCase();
              if (!query) return options;
              return options.filter((contribution) => [
                contribution.venue,
                contribution.layoutName,
                contribution.location?.locality,
                contribution.location?.region,
                contribution.location?.countryCode,
              ].some((value) => value?.toLocaleLowerCase().includes(query)));
            }}
            onChange={(_, contribution) => {
              const contributionId = contribution?.id ?? null;
              setSelectedContributionId(contributionId);
              void saveAssignment({ contributionId });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search any circuit or layout"
                error={selectedContributionMissing}
                helperText={selectedContributionMissing ? 'The assigned circuit is unavailable.' : undefined}
              />
            )}
          />
        </FormControl>

        {suggestedCircuit && selectedContributionId !== suggestedCircuit.id && !circuitMatch ? (
          <Button
            size="small"
            variant="text"
            disabled={saving}
            onClick={() => {
              setSelectedContributionId(suggestedCircuit.id);
              void saveAssignment({ contributionId: suggestedCircuit.id });
            }}
            sx={{ alignSelf: 'flex-start', mt: -0.35, textTransform: 'none', px: 0, fontSize: helperFontSize }}
          >
            Use suggested · {suggestedCircuit.venue?.trim() || 'Saved circuit'}
          </Button>
        ) : null}

        {suggestedCircuitMatch && selectedContributionId !== suggestedCircuitMatch.draftId ? (
          <Button
            size="small"
            variant="text"
            disabled={saving}
            onClick={() => {
              setSelectedContributionId(suggestedCircuitMatch.draftId);
              void saveAssignment({ contributionId: suggestedCircuitMatch.draftId });
            }}
            sx={{ alignSelf: 'flex-start', mt: -0.35, textTransform: 'none', px: 0, fontSize: helperFontSize }}
          >
            Use GPS match · {suggestedCircuitMatch.circuitName}
          </Button>
        ) : null}

        <FormControl size="small" fullWidth>
          <Typography sx={{ fontSize: labelFontSize, fontWeight: 650, mb: 0.35, color: '#94A3B8' }}>
            Driver
          </Typography>
          <Select
            value={selectedDriverId ?? ''}
            displayEmpty
            disabled={driversLoading || saving}
            onChange={(event) => {
              void handleSidebarDriverChange(event.target.value || null);
            }}
          >
            {allowClearDriver ? <MenuItem value="">Unassigned</MenuItem> : null}
            {selectedDriverMissing ? (
              <MenuItem value={selectedDriverId!} disabled>
                Assigned driver
              </MenuItem>
            ) : null}
            {drivers.map((driver) => (
              <MenuItem
                key={driver.id}
                value={driver.id}
                disabled={driverSelectionLocked && driver.id !== drivers[0]?.id}
              >
                {driver.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <Typography sx={{ fontSize: labelFontSize, fontWeight: 650, mb: 0.35, color: '#94A3B8' }}>
            Vehicle
          </Typography>
          <Select
            value={selectedKartId ?? ''}
            displayEmpty
            disabled={saving}
            onChange={(event) => {
              const kartId = event.target.value || null;
              setSelectedKartId(kartId);
              setSelectedKartSetupHistoryEntryId(null);
              void saveAssignment({ kartId, kartSetupHistoryEntryId: null });
            }}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {selectedKartMissing ? (
              <MenuItem value={selectedKartId!} disabled>
                Assigned vehicle
              </MenuItem>
            ) : null}
            {visibleKarts.map((kart) => (
              <MenuItem key={kart.id} value={kart.id}>
                {kart.kartNumber ? `#${kart.kartNumber}` : kart.nickname ?? 'Vehicle'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {saving ? (
          <Typography sx={{ fontSize: metaFontSize, color: '#64748B' }}>Saving…</Typography>
        ) : null}

        <SessionKartSetupPanel
          kart={activeKart}
          recordedAtMs={recordedAtMs}
          setupHistoryEntryId={selectedKartSetupHistoryEntryId}
          variant="compact"
        />
      </Stack>
    );
  }

  if (!canEdit) {
    const driverName = drivers.find((driver) => driver.id === contextDriverId)?.name ?? 'Unassigned';
    const kartLabel = activeKart
      ? (activeKart.kartNumber ? `#${activeKart.kartNumber}` : activeKart.nickname ?? 'Vehicle')
      : 'Unassigned';
    const assignedCircuit = assignableCircuits.find((contribution) => contribution.id === contextContributionId);
    const circuitName = assignedCircuit
      ? `${assignedCircuit.venue?.trim() || 'Saved circuit'} (${statusLabel(assignedCircuit.status)})`
      : 'Unassigned';

    return (
      <Stack spacing={1.25}>
        <Alert severity="info" sx={{ py: 0.35 }}>
          Session editing is read-only. Go to Billing to subscribe or apply a partner code.
        </Alert>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
          <Typography sx={{ fontSize: bodyFontSize, fontWeight: 800, mb: 0.5 }}>{assignmentTitle}</Typography>
          <Typography sx={{ fontSize: bodyFontSize, color: 'text.secondary' }}>Driver: {driverName}</Typography>
          <Typography sx={{ fontSize: bodyFontSize, color: 'text.secondary' }}>Vehicle: {kartLabel}</Typography>
          <Typography sx={{ fontSize: bodyFontSize, color: 'text.secondary' }}>Circuit: {circuitName}</Typography>
          {activeKart ? (
            <Typography sx={{ fontSize: metaFontSize, color: 'text.secondary', mt: 0.25 }}>
              {formatKartSubtitle(activeKart)}
            </Typography>
          ) : null}
        </Box>
        <SessionKartSetupPanel
          kart={activeKart}
          recordedAtMs={recordedAtMs}
          setupHistoryEntryId={selectedKartSetupHistoryEntryId}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25}>
      {variant === 'default' ? (
        <>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>{assignmentTitle}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            Link this session to the driver, vehicle, and circuit that ran it. Setup below reflects the vehicle configuration at session time.
          </Typography>
        </>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!hideCircuitAssignment && suggestedCircuitMatch && selectedContributionId === suggestedCircuitMatch.draftId ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Matched {suggestedCircuitMatch.circuitName} from GPS (
          {Math.round(suggestedCircuitMatch.confidence * 100)}% confidence)
        </Alert>
      ) : null}

      {!hideCircuitAssignment ? <FormControl size="small" fullWidth>
        <Typography sx={fieldLabelSx}>Assigned circuit</Typography>
        <Typography sx={fieldHelperSx}>
          Saved circuit geometry for map overlay and start/finish lap timing.
        </Typography>
        <Select
          value={selectedContributionId ?? ''}
          displayEmpty
          disabled={contributionsLoading}
          onChange={(event) => setSelectedContributionId(event.target.value || null)}
          sx={drawerSelectSx}
        >
          <MenuItem value="">Unassigned</MenuItem>
          {selectedContributionMissing ? (
            <MenuItem value={selectedContributionId!} disabled>
              Assigned circuit
            </MenuItem>
          ) : null}
          {circuitPickerOptions.map((entry) => (
            <MenuItem key={entry.contribution.id} value={entry.contribution.id}>
              {formatCircuitPickerMenuLabel(entry.contribution, { distanceKm: entry.distanceKm })}
            </MenuItem>
          ))}
        </Select>
        {assignedContributionLabel ? (
          <Typography sx={fieldMetaSx}>
            Selected: {assignedContributionLabel}
          </Typography>
        ) : null}
        {suggestedCircuit && selectedContributionId !== suggestedCircuit.id && !circuitMatch ? (
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectedContributionId(suggestedCircuit.id)}
            sx={{ alignSelf: 'flex-start', mt: 0.5, textTransform: 'none', px: 0 }}
          >
            Use suggested · {suggestedCircuit.venue?.trim() || 'Saved circuit'}
          </Button>
        ) : null}
        {suggestedCircuitMatch && selectedContributionId !== suggestedCircuitMatch.draftId ? (
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectedContributionId(suggestedCircuitMatch.draftId)}
            sx={{ alignSelf: 'flex-start', mt: 0.5, textTransform: 'none', px: 0 }}
          >
            Use suggested: {suggestedCircuitMatch.circuitName}
          </Button>
        ) : null}
      </FormControl> : null}

      <Box>
        <Typography sx={fieldLabelSx}>Driver</Typography>
        {compactVariant ? (
          <FormControl size="small" fullWidth>
            <Select
              value={selectedDriverId ?? ''}
              displayEmpty
              disabled={driversLoading}
              sx={drawerSelectSx}
              onChange={(event) => {
                const driverId = event.target.value || null;
                setSelectedDriverId(driverId);
                if (!driverId) {
                  setSelectedKartId(null);
                  setSelectedKartSetupHistoryEntryId(null);
                  return;
                }

                const driver = drivers.find((entry) => entry.id === driverId) ?? null;
                const driverKarts = filterKartsForDriver(karts, driver);
                const defaultKart = driverKarts[0];
                if (
                  defaultKart
                  && (!selectedKartId || !driverKarts.some((kart) => kart.id === selectedKartId))
                ) {
                  setSelectedKartId(defaultKart.id);
                  setSelectedKartSetupHistoryEntryId(null);
                }
              }}
            >
              {allowClearDriver ? <MenuItem value="">Unassigned</MenuItem> : null}
              {drivers.map((driver) => (
                <MenuItem
                  key={driver.id}
                  value={driver.id}
                  disabled={driverSelectionLocked && driver.id !== drivers[0]?.id}
                >
                  {driver.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {allowClearDriver ? (
              <Chip
                label="Unassigned"
                clickable
                color={selectedDriverId === null ? 'primary' : 'default'}
                onClick={() => {
                  setSelectedDriverId(null);
                  setSelectedKartId(null);
                  setSelectedKartSetupHistoryEntryId(null);
                }}
              />
            ) : null}
            {drivers.map((driver) => (
              <Chip
                key={driver.id}
                label={driver.name}
                clickable
                color={selectedDriverId === driver.id ? 'primary' : 'default'}
                onClick={() => {
                  if (driverSelectionLocked && driver.id !== drivers[0]?.id) {
                    return;
                  }

                  setSelectedDriverId(driver.id);
                  const defaultKart = filterKartsForDriver(karts, driver)[0];
                  if (
                    defaultKart
                    && (!selectedKartId || !filterKartsForDriver(karts, driver).some((kart) => kart.id === selectedKartId))
                  ) {
                    setSelectedKartId(defaultKart.id);
                    setSelectedKartSetupHistoryEntryId(null);
                  }
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      <FormControl size="small" fullWidth>
        <Typography sx={fieldLabelSx}>Vehicle</Typography>
        <Select
          value={selectedKartId ?? ''}
          displayEmpty
          sx={drawerSelectSx}
          onChange={(event) => {
            setSelectedKartId(event.target.value || null);
            setSelectedKartSetupHistoryEntryId(null);
          }}
        >
          <MenuItem value="">Unassigned</MenuItem>
          {visibleKarts.map((kart) => (
            <MenuItem key={kart.id} value={kart.id}>
              {kart.kartNumber ? `#${kart.kartNumber}` : kart.nickname ?? 'Vehicle'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        disabled={saving}
        onClick={() => void handleSave()}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          ...(variant === 'drawer'
            ? {
                color: drawerChrome.title,
                borderColor: drawerChrome.inputBorder,
                '&:hover': {
                  borderColor: drawerChrome.inputBorderHover,
                  bgcolor: drawerChrome.resetHoverBg,
                },
              }
            : {}),
        }}
      >
        {saving ? 'Saving…' : 'Save session details'}
      </Button>

      {showVehicleSetup ? <SessionKartSetupPanel
        kart={activeKart}
        recordedAtMs={recordedAtMs}
        setupHistoryEntryId={selectedKartSetupHistoryEntryId}
        onSetupHistoryEntryChange={(entryId) => {
          setSelectedKartSetupHistoryEntryId(entryId);
          void saveAssignment({ kartSetupHistoryEntryId: entryId });
        }}
        saving={saving}
        onOpenVehicle={
          activeKart && canManageVehicle
            ? () => navigate(`/karts?vehicle=${activeKart.id}`)
            : undefined
        }
      /> : null}
    </Stack>
  );
}
